import { flatten } from 'flat';
import { createRoot, Root } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { HashRouter } from 'react-router-dom';
import App from './App';
import ErrorBoundary from './components/atoms/ErrorBoundary';
import { getLinksForTranslation } from './components/atoms/Link';
import LoadingIndicator from './components/atoms/LoadingIndicator';
import setupMockApi from './mockApi';
import { setCookieConsent } from './utils/abTests';
import { AppQualifier, getAppQualifier } from './utils/appQualifier';
import callApi, { ApiCallOptions } from './utils/callApi';
import { CheckoutState, CheckoutStateProvider, setBasketId } from './utils/checkoutState';
import { Config, config, DISTRIBUTION_URL, getCdnShopUrl, loadConfig, setProvisionalConfig } from './utils/config';
import { decodeAltLoyaltyId, decodeBasketId, decodeCampaignKey } from './utils/decodeJwtPayload';
import formatLocale from './utils/formatLocale';
import { Mutations } from './utils/getMutations';
import { isEmpty } from './utils/getValidator';
import { ModalProvider } from './utils/modal';
import { NotificationsProvider } from './utils/notifications';
import observeAttribute from './utils/observeAttribute';
import { preloadAssets } from './utils/preloadAssets';
import { parseCustomData, parsePreferredCollectionPoint, parseStringValue } from './utils/putStateProps';
import sendDatadogEvent from './utils/sendDatadogEvent';
import setCofeNextAbTest, { AbTesting } from './utils/setCofeNextAbTest';
import { pushErrorTrackingEventFromErrorObject } from './utils/tracking';
import { getDevice, setDevice } from './utils/useIsMobileDevice';
import { wait } from './utils/wait';
import './globalStyles/global.scss';

if (USE_MOCK_API) {
  setupMockApi();
}

let freshTokenState: CheckoutState | undefined;
declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    ayCheckout?: {
      appQualifier?: AppQualifier;
      abTesting?: AbTesting;
      customer?: { firstName: string; lastName: string; email: string };
      initialBackendConfig?: Record<string, any>;
      startTimer?: number;
    };
    ayCheckoutFunctions?: {
      dispatchEvent?: EventTarget['dispatchEvent'];
    };
    scayleCheckoutEvents: EventTarget;
    scayleCheckoutFunctions: {
      filterPaymentOptions: FilterPaymentOptions;
      sortPaymentOptions: SortPaymentOptions;
      callMiddlewareApi: <T = any>(options: ApiCallOptions) => Promise<T>;
      mutations?: Mutations;
    };
    scayleCheckout?: {
      config?: Config;
    };
  }
}

class CheckoutWebComponent extends HTMLElement {
  reactRoot: Root | undefined;
  isAuthenticated = false;
  isInitialUpdateDone = false;
  shouldRetryState = true;

  async connectedCallback() {
    const shadowRoot = import.meta.env.DEV ? this : this.attachShadow({ mode: 'open' });

    // Get app qualifier and basket ID from component attributes
    const injectedAppQualifier = window.ayCheckout?.appQualifier;
    const appQualifier = getAppQualifier({ element: this, injectedAppQualifier });
    const urlParams = new URLSearchParams(window.location.search);

    // Signed options as JWT
    const jwt = this.getAttribute('jwt') ?? undefined;
    // Allow qaBasketId through query param for use with basket generator
    const basketId =
      decodeBasketId(jwt) ??
      urlParams.get('qaBasketId') ??
      this.getAttribute('basket-id') ??
      this.getAttribute('basketId') ??
      undefined;
    // For when the storefront already has a valid session token
    const sessionToken = this.getAttribute('session') ?? undefined;
    // Access token of new auth service
    const accessToken =
      urlParams.get('accessToken') ??
      this.getAttribute('access-token') ??
      this.getAttribute('accessToken') ??
      undefined;
    // For tracking
    const correlationId = this.getAttribute('correlation-id') ?? this.getAttribute('correlationId') ?? undefined;
    // For campaign vouchers that should be pre-applied in the checkout
    const voucher = this.getAttribute('voucher') ?? undefined;
    // For campaignKey that should be pre-applied in the checkout
    const campaignKey =
      decodeCampaignKey(jwt) ?? this.getAttribute('campaign-key') ?? this.getAttribute('campaignKey') ?? undefined;
    // For order level custom data
    const customData = this.getAttribute('custom-data') ?? this.getAttribute('customData') ?? undefined;
    // For customer preferred collection point
    const preferredCollectionPoint =
      this.getAttribute('preferred-collection-point') ?? this.getAttribute('preferredCollectionPoint') ?? undefined;
    // Header element CSS selector used to get top offset for notification display
    const headerElement = this.getAttribute('header-element') ?? this.getAttribute('headerElement') ?? undefined;
    // Prevent css overwrites from loading
    const hideCssOverwrites = urlParams.get('hideCssOverwrites') === 'true';
    // Cookie consent for eg. a/b tests set by the shop application
    const cookieConsent = this.getAttribute('has-cookie-consent') === 'true';
    // When Express Checkout is used and we need to render Review Page
    const transactionId = this.getAttribute('transaction-id') ?? this.getAttribute('transactionId') ?? undefined;
    // For alternative loyalty ID that should be pre-applied in the checkout
    const altLoyaltyId =
      decodeAltLoyaltyId(jwt) ?? this.getAttribute('alt-loyalty-id') ?? this.getAttribute('altLoyaltyId') ?? undefined;

    // Expose dispatchEvent
    window.ayCheckoutFunctions = { dispatchEvent: e => this.dispatchEvent(e) };
    window.scayleCheckoutEvents = new EventTarget();
    window.scayleCheckoutFunctions = {
      ...window.scayleCheckoutFunctions
    };

    if (window.ayCheckout?.abTesting) {
      setCofeNextAbTest(window.ayCheckout.abTesting);
    }

    setProvisionalConfig({
      appQualifier,
      session: { token: sessionToken, accessToken, correlationId, altLoyaltyId },
      transactionId
    });
    setCookieConsent(cookieConsent);
    setDevice(appQualifier);

    const alwaysTriggerAuthenticateEvent = urlParams.get('coAuthenticate') === 'true';
    if (alwaysTriggerAuthenticateEvent) {
      const url = new URL(window.location.href);
      url.searchParams.delete('coAuthenticate');
      window.history.pushState(null, '', url.toString());
    }

    const loadConfigAndApplyCssVars = async () => {
      await loadConfig();
      Object.entries(config.styles).forEach(([key, value]) => this.style.setProperty(`--${key}`, value));
    };

    const loadBaseCss = () =>
      new Promise<void>(resolve => {
        const baseCss = document.createElement('link');
        baseCss.rel = 'stylesheet';
        baseCss.href = `${DISTRIBUTION_URL}/ay-checkout-base.min.css`;
        baseCss.addEventListener('load', () => resolve());
        shadowRoot.appendChild(baseCss);
      });

    const loadOverwritesCss = () => {
      // We don't actually wanna wait for this to be loaded so we don't make this a promise
      if (hideCssOverwrites) {
        return;
      }
      const overwritesCss = document.createElement('link');
      overwritesCss.rel = 'stylesheet';
      overwritesCss.href = `${getCdnShopUrl(appQualifier)}/overwrites.css`;
      shadowRoot.appendChild(overwritesCss);
    };

    const setupPaymentContainers = () => {
      // Add payment containers outside of shadow dom (Payment SDKs can't find elements inside the shadow dom)
      const adyenPaymentContainer = document.createElement('div');
      adyenPaymentContainer.slot = 'adyen-payment-container';
      adyenPaymentContainer.id = 'adyen-payment-container';
      this.appendChild(adyenPaymentContainer);
      const klarnaPaymentContainer = document.createElement('div');
      klarnaPaymentContainer.slot = 'klarna-payment-container';
      klarnaPaymentContainer.id = 'klarna-payment-container';
      this.appendChild(klarnaPaymentContainer);
      const arvatoPaymentContainer = document.createElement('div');
      arvatoPaymentContainer.slot = 'arvato-payment-container';
      arvatoPaymentContainer.id = 'arvato-payment-container';
      this.appendChild(arvatoPaymentContainer);
      // Unzer Ideal
      const unzerIdeal = document.createElement('div');
      unzerIdeal.slot = 'unzer-ideal-container';
      unzerIdeal.id = 'unzer-ideal-container';
      unzerIdeal.className = 'unzerUI form';
      this.appendChild(unzerIdeal);
      const unzerIdealSelect = document.createElement('div');
      unzerIdealSelect.slot = 'ideal-bank-select';
      unzerIdealSelect.id = 'ideal-bank-select';
      unzerIdealSelect.className = 'field';
      unzerIdeal.appendChild(unzerIdealSelect);
      // Unzer Installment
      const unzerInstallmentContainer = document.createElement('div');
      unzerInstallmentContainer.slot = 'unzer-installment-container';
      unzerInstallmentContainer.id = 'unzer-installment-container';
      unzerInstallmentContainer.className = 'unzerUI form';
      this.appendChild(unzerInstallmentContainer);
      const unzerInstallment = document.createElement('div');
      unzerInstallment.slot = 'unzer-installment-plan';
      unzerInstallment.id = 'unzer-installment-plan';
      unzerInstallmentContainer.appendChild(unzerInstallment);
      // Unzer Creditcard
      const unzerForm = document.createElement('div');
      unzerForm.slot = 'unzer-creditcard-container';
      unzerForm.id = 'unzer-creditcard-container';
      unzerForm.className = 'unzerUI form unzer-payment';
      this.appendChild(unzerForm);
      const unzerCardNumber = document.createElement('div');
      unzerCardNumber.slot = 'card-element-id-number';
      unzerCardNumber.id = 'card-element-id-number';
      unzerCardNumber.className = 'unzerInput';
      unzerForm.appendChild(unzerCardNumber);
      const unzerCardExpiry = document.createElement('div');
      unzerCardExpiry.slot = 'card-element-id-expiry';
      unzerCardExpiry.id = 'card-element-id-expiry';
      unzerCardExpiry.className = 'unzerInput';
      unzerForm.appendChild(unzerCardExpiry);
      const unzerCardCVC = document.createElement('div');
      unzerCardCVC.slot = 'card-element-id-cvc';
      unzerCardCVC.id = 'card-element-id-cvc';
      unzerCardCVC.className = 'unzerInput';
      unzerForm.appendChild(unzerCardCVC);
      const unzerCardHolder = document.createElement('div');
      unzerCardHolder.slot = 'card-element-id-holder';
      unzerCardHolder.id = 'card-element-id-holder';
      unzerCardHolder.className = 'unzerInput';
      unzerForm.appendChild(unzerCardHolder);
    };

    const createPasswordAutofillElement = () => {
      const passwordFieldInput = document.createElement('input');
      passwordFieldInput.type = 'password';
      passwordFieldInput.style.display = 'none';
      this.appendChild(passwordFieldInput);
    };

    const onUpdate = (checkoutState: CheckoutState) => {
      const isAuthenticated = !!checkoutState.customer && !checkoutState.customer.flags?.isGuest;
      if (isAuthenticated !== this.isAuthenticated) {
        this.isAuthenticated = isAuthenticated;
        if (alwaysTriggerAuthenticateEvent || this.isInitialUpdateDone) {
          this.dispatchEvent(new CustomEvent('authenticate', { detail: checkoutState.customer }));
        }
      }
      this.dispatchEvent(new CustomEvent('update', { detail: checkoutState }));
      if (!this.isInitialUpdateDone) {
        this.isInitialUpdateDone = true;
      }
    };

    const onError = (error: Error) => {
      this.dispatchEvent(new CustomEvent('error', { detail: error }));
    };

    const handleAccessTokenChange = async (newAccessToken: string | null) => {
      if (newAccessToken) {
        config.session.accessToken = newAccessToken;
        const lastRequestString = sessionStorage.getItem('lastRequest');
        if (lastRequestString) {
          const lastRequest: ApiCallOptions = JSON.parse(lastRequestString);
          try {
            const newCheckoutState = await callApi(lastRequest);
            freshTokenState = newCheckoutState;
          } catch (error) {
            console.error(error);
          } finally {
            sessionStorage.removeItem('lastRequest');
          }
        }
      }
    };

    observeAttribute(this, ['access-token', 'accessToken'], handleAccessTokenChange);

    const urlSearchParams = new URLSearchParams(window.location.search);
    const enableRuleEngineDebug = urlSearchParams.get('enableRuleEngineDebug') ?? undefined;
    const isWaitingForPaymentConfirmation = urlSearchParams.get('waitForPaymentConfirmation');
    const shouldHidePaymentMethods =
      urlSearchParams.get('withoutPayments') === '1' || urlSearchParams.get('withoutPayments') === 'true';
    const device = getDevice();

    const loadInitialCheckoutState: (providedBasketId?: string) => Promise<CheckoutState> = async providedBasketId => {
      const isReviewPage = !!transactionId;
      if (isReviewPage) {
        try {
          return await callApi<CheckoutState>({
            endpoint: '/state',
            failedStatusCodes: [204],
            sequential: true
          });
        } catch (e: any) {
          console.error(e);
          throw e;
        }
      } else {
        const _basketId = providedBasketId ?? basketId;
        try {
          return await callApi<CheckoutState>({
            endpoint: '/state',
            method: 'PUT',
            payload: {
              device,
              basketId: !isEmpty(_basketId) ? _basketId : undefined,
              voucher: !isEmpty(voucher) && voucher !== 'null' ? voucher : undefined,
              campaignKey: parseStringValue(campaignKey),
              altLoyaltyId: parseStringValue(altLoyaltyId),
              customData: parseCustomData(customData),
              preferredCollectionPoint: parsePreferredCollectionPoint(preferredCollectionPoint),
              jwt: !isEmpty(jwt) ? jwt : undefined,
              ...(enableRuleEngineDebug && {
                ruleEngineDebugger: !!Number(enableRuleEngineDebug)
              }),
              ...(shouldHidePaymentMethods && {
                withoutPayments: true
              })
            },
            failedStatusCodes: [204],
            sequential: true
          });
        } catch (e: any) {
          e.endpoint = 'PUT /co/api/v3/state';
          if (![204, 403, 404].includes(e.statusCode) && this.shouldRetryState) {
            this.shouldRetryState = false;
            await wait(35);
            return loadInitialCheckoutState();
          }

          if (!navigator.onLine) {
            e.message = 'The user has no internet connection';
          }

          throw e;
        }
      }
    };

    const initialize = async () => {
      try {
        if (isEmpty(basketId) && isEmpty(jwt) && !transactionId) {
          throw new Error('basket-id or jwt attribute missing on CO web component. CO session cannot be initiated.');
        }
        const [initialCheckoutState] = await Promise.all([
          !isWaitingForPaymentConfirmation ? loadInitialCheckoutState() : undefined,
          loadConfigAndApplyCssVars(),
          !import.meta.env.DEV && loadBaseCss(),
          loadOverwritesCss(),
          !import.meta.env.DEV && setupPaymentContainers(),
          createPasswordAutofillElement()
        ]);

        return { isReloading: false, isInitialBasketEmpty: false, initialCheckoutState };
      } catch (e: any) {
        if ([204, 404, 410].includes(e.statusCode)) {
          return { isReloading: false, isInitialBasketEmpty: true };
        } else if (e.statusCode === 401) {
          return { isReloading: true, isInitialBasketEmpty: false };
        } else if (e.statusCode === 403) {
          return { isReloading: false, isInitialBasketEmpty: false };
        }
        console.error(e);
        pushErrorTrackingEventFromErrorObject(e, 'initiate_checkout_session');

        return { initialRequestError: e, isReloading: false, isInitialBasketEmpty: false };
      }
    };

    const mountPoint = document.createElement('div');
    mountPoint.setAttribute('id', 'root');
    mountPoint.setAttribute('dir', ['ar', 'he'].includes(config.locale.substring(0, 2)) ? 'rtl' : 'ltr');
    shadowRoot.appendChild(mountPoint);
    this.reactRoot = createRoot(mountPoint);
    this.reactRoot.render(<LoadingIndicator />);

    const { initialRequestError, isReloading, isInitialBasketEmpty, initialCheckoutState } = await initialize();

    if (isReloading) {
      return;
    }

    preloadAssets();

    const isEnteringFromFailedPayment = ({ notifications }: Pick<CheckoutState, 'notifications'>) =>
      notifications?.some(({ textTranslationKey }) => textTranslationKey?.includes('payment.notifications'));
    const isAmazonPayReviewPage = !!new URLSearchParams(window.location.search).get('isAmazonPayReviewPage');
    const isNormalCheckoutPage = initialCheckoutState && !isAmazonPayReviewPage;
    const isInitialLoadSuccess = isInitialBasketEmpty || isWaitingForPaymentConfirmation || isNormalCheckoutPage;

    if (initialRequestError) {
      onError(initialRequestError);
    } else if (isInitialLoadSuccess) {
      this.dispatchEvent(new CustomEvent('load'));
      this.setAttribute('load-success', '');

      if (isNormalCheckoutPage && !isEnteringFromFailedPayment(initialCheckoutState)) {
        try {
          sendDatadogEvent('checkout-load');
        } catch (error) {
          console.log(error);
        }
      }
    }

    this.reactRoot.render(
      <IntlProvider
        messages={flatten(config.translations)}
        locale={formatLocale(config.locale)}
        defaultRichTextElements={{
          b: chunks => <b>{chunks}</b>,
          strong: chunks => <strong>{chunks}</strong>,
          ...getLinksForTranslation()
        }}
      >
        <NotificationsProvider headerElement={headerElement}>
          <CheckoutStateProvider
            initialCheckoutState={initialCheckoutState}
            freshTokenState={freshTokenState}
            isInitialBasketEmpty={isInitialBasketEmpty}
            isWaitingForPaymentConfirmation={!!isWaitingForPaymentConfirmation}
            reloadInitialCheckoutState={loadInitialCheckoutState}
            onUpdate={onUpdate}
            initialRequestError={initialRequestError}
            jwt={jwt}
          >
            <ModalProvider>
              <HashRouter>
                <ErrorBoundary>
                  <App />
                </ErrorBoundary>
              </HashRouter>
            </ModalProvider>
          </CheckoutStateProvider>
        </NotificationsProvider>
      </IntlProvider>
    );
  }

  disconnectedCallback() {
    this.reactRoot?.unmount();
  }

  setBasketId(newBasketId: string) {
    // If a qaBasketId is supplied via query params we want to keep using that (needed for use with basket generator)
    if (new URLSearchParams(window.location.search).get('qaBasketId')) {
      return;
    }
    setBasketId(newBasketId);
  }
}

if (!window.customElements.get('scayle-checkout')) {
  window.customElements.define('scayle-checkout', CheckoutWebComponent);
}

// The official web component name is `scayle-checkout` but this is being left in here to maintain backwards compatibility
// The class needs to be duplicated as one class can apparently only be used once in one custom elements registry
class AyCheckoutWebComponent extends CheckoutWebComponent {}

if (!window.customElements.get('ay-checkout')) {
  window.customElements.define('ay-checkout', AyCheckoutWebComponent);
}
